import { http } from '../../http';
import { useCustomConfig } from '@/js/composables/useCustomAxiosRequestConfig';
import { __ } from '@/js/stores/expressions';
import type { ExpressionKey } from '@/js/consts/language/en';

export enum KeyIndicateSyncDataType {
    Agents = 1,
    Interactions = 2,
    Transcriptions = 3,
    TranscriptionAIMetaData = 4,
    TranscriptionCategories = 5,
    TranscriptionSegmentTrackers = 6,
    InteractionPartCustomMetadata = 8,
    CoachingForm = 9,
    CoachingProtocol = 10,
}

export type KeyIndicateSyncDataTypeInfo = {
    dataType: KeyIndicateSyncDataType;
    name: string;
    isEnabled: boolean;
    defaultDaysBack: number;
};

const labelKeys: Partial<Record<KeyIndicateSyncDataType, ExpressionKey>> = {
    [KeyIndicateSyncDataType.Agents]: 'back-office-sync-data-type-agents',
    [KeyIndicateSyncDataType.Interactions]: 'back-office-sync-data-type-interactions',
    [KeyIndicateSyncDataType.Transcriptions]: 'back-office-sync-data-type-transcriptions',
    [KeyIndicateSyncDataType.TranscriptionAIMetaData]:
        'back-office-sync-data-type-transcription-ai-metadata',
    [KeyIndicateSyncDataType.TranscriptionCategories]:
        'back-office-sync-data-type-transcription-categories',
    [KeyIndicateSyncDataType.TranscriptionSegmentTrackers]:
        'back-office-sync-data-type-transcription-segment-trackers',
    [KeyIndicateSyncDataType.InteractionPartCustomMetadata]:
        'back-office-sync-data-type-interaction-part-custom-metadata',
    [KeyIndicateSyncDataType.CoachingForm]: 'back-office-sync-data-type-coaching-form',
    [KeyIndicateSyncDataType.CoachingProtocol]: 'back-office-sync-data-type-coaching-protocol',
};

// A data type without a translation shows its backend name, so a new type works before it is translated.
export const getKeyIndicateSyncDataTypeLabel = (info: KeyIndicateSyncDataTypeInfo) => {
    const key = labelKeys[info.dataType];
    return key ? __(key) : info.name;
};

export const MAX_DAYS_BACK = 365;

export const isValidDaysBack = (daysBack: number) =>
    Number.isInteger(daysBack) && daysBack >= 1 && daysBack <= MAX_DAYS_BACK;

export type KeyIndicateSyncSetting = {
    dataType: KeyIndicateSyncDataType;
    isEnabled: boolean;
    daysBack: number;
};

export type KeyIndicateSyncDataTypeSelection = {
    dataType: KeyIndicateSyncDataType;
    daysBack: number;
};

export type KeyIndicateSyncSystem = {
    tenantExternalSystemId: string;
    systemName: string;
    keyIndicateExternalSystemId: number;
    settings: KeyIndicateSyncSetting[];
    lastRunStartedAt: string | null;
    lastRunEndedAt: string | null;
    lastSuccessAt: string | null;
};

export type KeyIndicateSyncRunStatus = 'running' | 'succeeded' | 'failed';

/** A successful run sets lastSuccessAt to that run's start time, so a mismatch means the latest run failed. */
export const getKeyIndicateSyncRunStatus = (
    system: KeyIndicateSyncSystem
): KeyIndicateSyncRunStatus | null => {
    if (!system.lastRunStartedAt) {
        return null;
    }

    if (
        !system.lastRunEndedAt ||
        new Date(system.lastRunEndedAt) < new Date(system.lastRunStartedAt)
    ) {
        return 'running';
    }

    return system.lastSuccessAt === system.lastRunStartedAt ? 'succeeded' : 'failed';
};

export const getKeyIndicateSyncSystems = async (
    tenantId: string
): Promise<KeyIndicateSyncSystem[]> => {
    return http
        .backoffice()
        .get<KeyIndicateSyncSystem[]>(`/key-indicate/sync-settings/tenants/${tenantId}`)
        .then((response) => response.data);
};

export const saveKeyIndicateSyncSystemSettings = async (
    tenantExternalSystemId: string,
    settings: KeyIndicateSyncSetting[]
): Promise<void> => {
    return http
        .backoffice()
        .put(
            `/key-indicate/sync-settings/systems/${tenantExternalSystemId}`,
            settings,
            // silent: the mutation's onError shows the backend's actual `detail` message.
            useCustomConfig({ silent: true })
        )
        .then(() => undefined);
};

export const getKeyIndicateSyncDataTypes = async (): Promise<KeyIndicateSyncDataTypeInfo[]> => {
    return http
        .backoffice()
        .get<KeyIndicateSyncDataTypeInfo[]>('/key-indicate/sync-settings/data-types')
        .then((response) => response.data);
};

export const setKeyIndicateSyncDataTypeEnabled = async (
    dataType: KeyIndicateSyncDataType,
    isEnabled: boolean
): Promise<void> => {
    return http
        .backoffice()
        .put(
            `/key-indicate/sync-settings/data-types/${dataType}`,
            { isEnabled },
            useCustomConfig({ silent: true })
        )
        .then(() => undefined);
};
